/**
 * Scripted, deterministic tutorial lessons.
 *
 * Each lesson builds a starting position with the engine's scenario builder and
 * then hands control back to the normal rules: the learner plays real Mahjong,
 * never a slideshow. Lessons never mutate a live game.
 *
 * A lesson holds no words. Its `id` is internal and identical in every
 * language; title, objective, intro, task and success line live in
 * @kikik/i18n, so an Indonesian and an English learner meet exactly the same
 * position.
 */

import { TILE_INFO, TILE_KINDS, applyAction, createScenario, getPlayer } from "@kikik/mahjong-core";
import type { GameState, PlayerId, ScenarioPlayerSpec, TileKind } from "@kikik/mahjong-core";
import { LESSON_IDS } from "@kikik/i18n";
import type { LessonId } from "@kikik/i18n";

export { LESSON_IDS };
export type { LessonId };

export interface Lesson {
  /** Internal id. The copy for it is looked up per language. */
  id: LessonId;
  /** Build the starting position. Always the same tiles, every time. */
  createState(): GameState;
  /** Has the learner done what the lesson asks? */
  isComplete(state: GameState): boolean;
}

const HUMAN: PlayerId = 0;

/** Tiles that cannot claim `discard` and never form a pair inside one hand. */
function fillerHand(discard: TileKind | null, remaining: Map<TileKind, number>): TileKind[] {
  const info = discard ? TILE_INFO[discard] : null;
  const hand: TileKind[] = [];
  for (const kind of TILE_KINDS) {
    if (hand.length === 13) break;
    if ((remaining.get(kind) ?? 0) <= 0) continue;
    if (discard && kind === discard) continue;
    if (info && !info.isHonor) {
      const other = TILE_INFO[kind];
      if (!other.isHonor && other.suit === info.suit && Math.abs(other.rank - info.rank) <= 2) {
        continue;
      }
    }
    hand.push(kind);
    remaining.set(kind, (remaining.get(kind) ?? 0) - 1);
  }
  return hand;
}

interface LessonSetup {
  human: ScenarioPlayerSpec;
  /** Seat that throws the tile the lesson is about. */
  discarder?: PlayerId;
  discard?: TileKind;
  /** Tiles at the front of the wall, in draw order. */
  wall?: TileKind[];
  turn?: PlayerId;
}

function buildState(setup: LessonSetup): GameState {
  const remaining = new Map<TileKind, number>(TILE_KINDS.map((kind) => [kind, 4]));
  const spend = (kinds: readonly TileKind[]): void => {
    for (const kind of kinds) remaining.set(kind, (remaining.get(kind) ?? 0) - 1);
  };

  spend(setup.human.hand ?? []);
  if (setup.human.drawn) spend([setup.human.drawn]);
  for (const meld of setup.human.melds ?? []) spend(meld.tiles);
  if (setup.discard) spend([setup.discard]);
  spend(setup.wall ?? []);

  const players: ScenarioPlayerSpec[] = [setup.human];
  for (const seat of [1, 2, 3] as PlayerId[]) {
    const hand = fillerHand(setup.discard ?? null, remaining);
    players[seat] =
      seat === setup.discarder ? { hand, drawn: setup.discard ?? undefined } : { hand };
  }

  const state = createScenario({
    seed: "kikik-tutorial",
    turn: setup.turn ?? (setup.discarder ?? HUMAN),
    players,
    ...(setup.wall ? { wall: setup.wall } : {}),
  });

  if (setup.discarder === undefined || !setup.discard) return state;

  // Play the scripted discard through the engine so the claim window is real.
  const tile = getPlayer(state, setup.discarder).drawnTile;
  const result = applyAction(state, {
    type: "discard",
    playerId: setup.discarder,
    tileId: tile?.id ?? -1,
  });
  if (!result.ok) throw new Error(`Tutorial setup failed: ${result.error.code}`);
  return result.state;
}

function humanHasMeld(state: GameState, kind: "chow" | "pong" | "kong"): boolean {
  return getPlayer(state, HUMAN).melds.some((meld) => meld.kind === kind);
}

const READY_HAND: TileKind[] = [
  "CHA1", "CHA2", "CHA3",
  "BAM4", "BAM5", "BAM6",
  "DOT7", "DOT8", "DOT9",
  "WIND_EAST", "WIND_EAST", "WIND_EAST",
  "DRAGON_RED",
];

export const TUTORIAL_LESSONS: readonly Lesson[] = [
  {
    id: "dasar",
    createState: () =>
      buildState({
        human: {
          hand: [
            "CHA2", "CHA3", "CHA4",
            "BAM5", "BAM5", "BAM6",
            "DOT2", "DOT3", "DOT7",
            "DOT8", "DRAGON_GREEN", "DRAGON_GREEN",
            "BAM9",
          ],
          drawn: "WIND_NORTH",
        },
      }),
    isComplete: (state) => getPlayer(state, HUMAN).discards.length > 0,
  },
  {
    id: "ambil-buang",
    createState: () =>
      buildState({
        human: {
          hand: [
            "CHA2", "CHA3", "CHA4",
            "BAM5", "BAM5", "BAM6",
            "DOT2", "DOT3", "DOT7",
            "DOT8", "DRAGON_GREEN", "DRAGON_GREEN",
            "BAM9",
          ],
        },
        turn: HUMAN,
        wall: ["CHA9"],
      }),
    isComplete: (state) => getPlayer(state, HUMAN).discards.length > 0,
  },
  {
    id: "chow",
    createState: () =>
      buildState({
        human: {
          hand: [
            "BAM3", "BAM4", "CHA2",
            "CHA3", "CHA4", "DOT2",
            "DOT3", "DOT4", "DRAGON_GREEN",
            "DRAGON_GREEN", "WIND_SOUTH", "CHA8",
            "CHA9",
          ],
        },
        discarder: 3,
        discard: "BAM5",
      }),
    isComplete: (state) => humanHasMeld(state, "chow"),
  },
  {
    id: "pong",
    createState: () =>
      buildState({
        human: {
          hand: [
            "DRAGON_RED", "DRAGON_RED", "CHA2",
            "CHA3", "CHA4", "DOT2",
            "DOT3", "DOT4", "BAM6",
            "BAM7", "BAM8", "CHA8",
            "WIND_NORTH",
          ],
        },
        discarder: 1,
        discard: "DRAGON_RED",
      }),
    isComplete: (state) => humanHasMeld(state, "pong"),
  },
  {
    id: "kong",
    createState: () =>
      buildState({
        human: {
          hand: [
            "WIND_WEST", "WIND_WEST", "WIND_WEST",
            "CHA2", "CHA3", "CHA4",
            "DOT2", "DOT3", "DOT4",
            "BAM6", "BAM7", "BAM8",
            "CHA9",
          ],
        },
        discarder: 2,
        discard: "WIND_WEST",
      }),
    isComplete: (state) => humanHasMeld(state, "kong"),
  },
  {
    id: "menang-sendiri",
    createState: () =>
      buildState({
        human: { hand: READY_HAND },
        turn: HUMAN,
        wall: ["DRAGON_RED"],
      }),
    isComplete: (state) =>
      state.result?.type === "win" && state.result.winner === HUMAN,
  },
  {
    id: "menang-dari-buangan",
    createState: () =>
      buildState({
        human: { hand: READY_HAND },
        discarder: 2,
        discard: "DRAGON_RED",
      }),
    isComplete: (state) =>
      state.result?.type === "win" && state.result.winner === HUMAN,
  },
];

export function getLesson(id: string): Lesson | null {
  return TUTORIAL_LESSONS.find((lesson) => lesson.id === id) ?? null;
}

export function nextLessonId(id: string): LessonId | null {
  const index = TUTORIAL_LESSONS.findIndex((lesson) => lesson.id === id);
  if (index === -1 || index + 1 >= TUTORIAL_LESSONS.length) return null;
  return TUTORIAL_LESSONS[index + 1]?.id ?? null;
}

/** 0-based position of a lesson in the journey, or -1 when unknown. */
export function lessonIndex(id: string): number {
  return TUTORIAL_LESSONS.findIndex((lesson) => lesson.id === id);
}
